'use client';

import { Box, Tooltip } from '@mui/material';
import type { SparkEngineStatus } from '@repo/api-gw-sdk';
import React from 'react';

interface SparkEngineStatusConfig {
  title: string;
  iconColor: string;
  backgroundColor: string;
  icon: string;
}

// Maps spark engine statuses to inventory-style icons and colors
const sparkEngineStatusConfig: Record<string, SparkEngineStatusConfig> = {
  CREATING: {
    title: 'Creating',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'material-symbols-avg-pace-rounded',
  },
  CREATE_FAILED: {
    title: 'Create Failed',
    iconColor: 'var(--mui-palette-error-main)',
    backgroundColor: 'var(--mui-palette-error-opacity)',
    icon: 'material-symbols-disc-full-outline-rounded',
  },
  STARTING: {
    title: 'Starting',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'material-symbols-avg-pace-rounded',
  },
  STARTED: {
    title: 'Started',
    iconColor: 'var(--mui-palette-success-main)',
    backgroundColor: 'var(--mui-palette-success-opacity)',
    icon: 'material-symbols-check-circle-outline-rounded',
  },
  STOPPING: {
    title: 'Stopping',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'material-symbols-avg-pace-rounded',
  },
  STOPPED: {
    title: 'Stopped',
    iconColor: 'var(--mui-palette-custom-GREY-01)',
    backgroundColor: 'var(--mui-palette-custom-GREY-OPACITY)',
    icon: 'material-symbols-do-not-disturb-on-outline-rounded',
  },
  DELETING: {
    title: 'Deleting',
    iconColor: 'var(--mui-palette-info-main)',
    backgroundColor: 'var(--mui-palette-info-opacity)',
    icon: 'material-symbols-avg-pace-rounded',
  },
};

export const SparkEngineStatusCell = React.forwardRef<
  HTMLSpanElement,
  {
    status: SparkEngineStatus;
    hideTooltip?: boolean;
  }
>(({ status, hideTooltip }, ref) => {
  const config = sparkEngineStatusConfig[status];
  if (!config) {
    return null;
  }

  return (
    <Tooltip title={hideTooltip ? undefined : config.title}>
      <Box
        ref={ref}
        component='span'
        className='rounded-full inline-flex p-[2px]'
        sx={{
          background: config.backgroundColor,
          '& i': {
            background: config.iconColor,
          },
        }}
      >
        <i className={`${config.icon} h-[20px] w-[20px]`} />
      </Box>
    </Tooltip>
  );
});

SparkEngineStatusCell.displayName = 'SparkEngineStatusCell';
